import React, { useState, useEffect } from 'react';
import TaskToggle from '../TaskToggle/TaskToggle';
import StatusChangePopup from '../StatusChangePopup/StatusChangePopup';
import './TasksPopup.css';

function TasksPopup({ contact, onClose, onTaskUpdate, onStatusChange }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'notes'
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [showStatusChangePopup, setShowStatusChangePopup] = useState(false);

  // Fetch tasks when popup opens
  useEffect(() => {
    if (contact) {
      fetchTasks();
    }
  }, [contact]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTasks = async () => {
    try {
      const response = await fetch(`/backend/api/contacts/tasks.php?contact_id=${contact.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks);
      } else {
        console.error('Error fetching tasks:', data.error);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskToggle = async (taskTypeId, newStatus) => {
    try {
      const response = await fetch('/backend/api/contacts/tasks.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: contact.id,
          task_type_id: taskTypeId,
          status: newStatus
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Update local state
        const updatedTasks = tasks.map(task => 
          task.task_type_id === taskTypeId 
            ? { ...task, status: newStatus, isCompleted: newStatus === 'done' }
            : task
        );
        setTasks(updatedTasks);

        // Check if this is the "Finished presentation" task being marked as done
        if (newStatus === 'done') {
          const task = updatedTasks.find(t => t.task_type_id === taskTypeId);
          if (task && task.code === 'finishes_presentation' && contact.status === 'lead') {
            setShowStatusChangePopup(true);
          }
        }

        // Calculate new counts and notify parent
        const totalTasks = updatedTasks.length;
        const doneTasks = updatedTasks.filter(task => task.status === 'done').length;
        
        if (onTaskUpdate) {
          onTaskUpdate(contact.id, doneTasks, totalTasks);
        }
      } else {
        console.error('Error updating task:', data.error);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // Status change handlers
  const handleStatusChangeClose = () => {
    setShowStatusChangePopup(false);
  };

  const handleStatusChangeConfirm = (contactId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(contactId, newStatus);
    }
    setShowStatusChangePopup(false);
  };

  // Notes handlers
  const handleNotesClick = (taskTypeId) => {
    const task = tasks.find(t => t.task_type_id === taskTypeId);
    setEditingTaskId(taskTypeId);
    setNoteText(task.notes || '');
    setIsEditingNotes(!task.notes); // Edit mode only if no notes exist
    setViewMode('notes');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditingTaskId(null);
    setNoteText('');
    setIsEditingNotes(false);
  };

  const handleEditNotes = () => {
    setIsEditingNotes(true);
  };

  const handleCancelEdit = () => {
    const task = tasks.find(t => t.task_type_id === editingTaskId);
    setNoteText(task.notes || '');
    setIsEditingNotes(false);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const response = await fetch('/backend/api/contacts/update-task-notes.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: contact.id,
          task_type_id: editingTaskId,
          notes: noteText
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update local state
          setTasks(tasks.map(t => 
            t.task_type_id === editingTaskId 
              ? { ...t, notes: noteText }
              : t
          ));
          setIsEditingNotes(false);  // Exit edit mode after save
          handleBackToList();
        }
      }
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteNotes = async () => {
    if (!window.confirm('Are you sure you want to delete these notes?')) {
      return;
    }
    setNoteText('');
    setSavingNotes(true);
    try {
      const response = await fetch('/backend/api/contacts/update-task-notes.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: contact.id,
          task_type_id: editingTaskId,
          notes: ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTasks(tasks.map(t => 
            t.task_type_id === editingTaskId 
              ? { ...t, notes: null }
              : t
          ));
          handleBackToList();
        }
      }
    } catch (error) {
      console.error('Error deleting notes:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  if (!contact) return null;

  return (
    <div className="tasks-popup-overlay" onClick={onClose}>
      <div className="tasks-popup" onClick={(e) => e.stopPropagation()}>
        {viewMode === 'list' ? (
          <>
            <div className="popup-header">
              <div className="header-content">
                <h2>{contact.first_name} {contact.last_name} - Tasks</h2>
                <span className={`stage-badge stage-${contact.status}`}>
                  {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)} Tasks
                </span>
              </div>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
            
            <div className="tasks-section">
              <h3>Pre-Registration Tasks</h3>
              {loading ? (
                <div className="loading">Loading tasks...</div>
              ) : (
                <div className="task-list">
                  {tasks.map(task => (
                    <TaskToggle
                      key={task.task_type_id}
                      task={task}
                      isCompleted={task.isCompleted}
                      onToggle={handleTaskToggle}
                      onNotesClick={handleNotesClick}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="popup-actions">
              <button className="close-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="popup-header">
              <button className="back-btn" onClick={handleBackToList}>
                ← Back
              </button>
              <h2>Task Notes</h2>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
            <div className="notes-editor-section">
              <h3>{tasks.find(t => t.task_type_id === editingTaskId)?.name}</h3>
              
              {isEditingNotes ? (
                <>
                  {/* Edit mode - show textarea */}
                  <textarea
                    className="notes-textarea"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value.slice(0, 300))}
                    placeholder="Add notes here... (optional)"
                    maxLength={300}
                  />
                  <div className="char-counter">
                    {300 - noteText.length} characters remaining
                  </div>
                  <div className="notes-actions">
                    <button 
                      className="save-btn" 
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                    >
                      {savingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    {tasks.find(t => t.task_type_id === editingTaskId)?.notes && (
                      <button 
                        className="cancel-btn" 
                        onClick={handleCancelEdit}
                        disabled={savingNotes}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Read mode - show saved notes as text */}
                  <div className="notes-display-box">
                    {noteText}
                  </div>
                  <div className="notes-actions">
                    <button 
                      className="edit-btn" 
                      onClick={handleEditNotes}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn" 
                      onClick={handleDeleteNotes}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showStatusChangePopup && (
        <StatusChangePopup
          contact={contact}
          onClose={handleStatusChangeClose}
          onStatusChange={handleStatusChangeConfirm}
        />
      )}
    </div>
  );
}

export default TasksPopup;
